import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { blogAPI } from '../../lib/api';
import { 
    Calendar, 
    Clock, 
    User, 
    Eye,
    ArrowRight,
    BookOpen,
    Search
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const BlogPage = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, [page, selectedCategory]);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const params = { page, limit: 9 };
            if (selectedCategory) params.category = selectedCategory;
            
            const response = await blogAPI.getPosts(params);
            setPosts(response.data.posts);
            setTotalPages(response.data.total_pages);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await blogAPI.getCategories();
            setCategories(response.data.categories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getCategoryColor = (category) => {
        const colors = {
            'career-tips': 'bg-blue-100 text-blue-700',
            'industry-news': 'bg-purple-100 text-purple-700',
            'job-search': 'bg-green-100 text-green-700',
            'interviews': 'bg-amber-100 text-amber-700',
            'workplace': 'bg-rose-100 text-rose-700',
        };
        return colors[category] || 'bg-neutral-100 text-neutral-700';
    };

    const filteredPosts = posts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Layout>
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6">
                            <BookOpen className="w-4 h-4" />
                            <span className="text-sm font-medium">Career Resources</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                            RecruitNearMe Blog
                        </h1>
                        <p className="text-lg text-neutral-200 max-w-2xl mx-auto mb-8">
                            Expert career advice, industry insights, and job search tips to help you land your dream role.
                        </p>
                        <div className="max-w-md mx-auto relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                            <Input
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-neutral-300"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="border-b bg-white sticky top-16 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 py-4 overflow-x-auto">
                        <Button
                            variant={selectedCategory === null ? "default" : "ghost"}
                            size="sm"
                            onClick={() => { setSelectedCategory(null); setPage(1); }}
                            className={selectedCategory === null ? "bg-primary-600" : ""}
                        >
                            All Posts
                        </Button>
                        {categories.map((cat) => (
                            <Button
                                key={cat.value}
                                variant={selectedCategory === cat.value ? "default" : "ghost"}
                                size="sm"
                                onClick={() => { setSelectedCategory(cat.value); setPage(1); }}
                                className={selectedCategory === cat.value ? "bg-primary-600" : ""}
                            >
                                {cat.label} ({cat.count})
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="py-12 bg-neutral-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                                    <div className="h-48 bg-neutral-200" />
                                    <div className="p-6 space-y-3">
                                        <div className="h-4 bg-neutral-200 rounded w-1/4" />
                                        <div className="h-6 bg-neutral-200 rounded w-3/4" />
                                        <div className="h-4 bg-neutral-200 rounded" />
                                        <div className="h-4 bg-neutral-200 rounded w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredPosts.length === 0 ? (
                        <div className="text-center py-16">
                            <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-neutral-700 mb-2">No posts found</h3>
                            <p className="text-neutral-500">Check back soon for new content!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post) => (
                                <article
                                    key={post.post_id}
                                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                                >
                                    <Link to={`/blog/${post.slug}`}>
                                        <div className="relative h-48 overflow-hidden">
                                            {(post.cover_image || post.featured_image) ? (
                                                <img
                                                    src={post.cover_image || post.featured_image}
                                                    alt={post.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                                                    <BookOpen className="w-12 h-12 text-white/50" />
                                                </div>
                                            )}
                                            <div className="absolute top-4 left-4">
                                                <Badge className={getCategoryColor(post.category)}>
                                                    {post.category.replace('-', ' ')}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            <h2 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                                                {post.title}
                                            </h2>
                                            <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                                                {post.excerpt}
                                            </p>
                                            <div className="flex items-center justify-between text-sm text-neutral-500">
                                                <div className="flex items-center gap-4">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4" />
                                                        {formatDate(post.published_at)}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Eye className="w-4 h-4" />
                                                        {post.views}
                                                    </span>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-primary-600 group-hover:translate-x-1 transition-transform" />
                                            </div>
                                        </div>
                                    </Link>
                                </article>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-2 mt-12">
                            <Button
                                variant="outline"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                Previous
                            </Button>
                            <span className="flex items-center px-4 text-sm text-neutral-500">
                                Page {page} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                disabled={page === totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </Layout>
    );
};

export default BlogPage;
